import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, statusOf } from "@/lib/api";
import { useLive, useLiveRefresh } from "@/lib/live";
import { dequeue, enqueue, isNetErr, newId, readQueue } from "@/lib/offlineQueue";

// Server state for one list + live refresh + polling fallback + an ordered offline queue (adds and check-offs).
export default function useListSync(hubId, listId, onEvent) {
  const base = `/organizations/${hubId}/lists/${listId}`;
  const { online } = useLive();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState(() => readQueue().filter((o) => o.listId === listId));
  const flushing = useRef(false);
  const evRef = useRef(onEvent);
  evRef.current = onEvent;

  const syncQueue = useCallback(() => setQueue(readQueue().filter((o) => o.listId === listId)), [listId]);
  const fetchNow = useCallback(async () => {
    try { setData((await api.get(base)).data); setError(null); } catch (e) { if (statusOf(e)) setError(e); }
  }, [base]);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    const ids = {};
    try {
      for (;;) {
        const op = readQueue().find((o) => o.listId === listId);
        if (!op) break;
        try {
          if (op.kind === "add") {
            const { data: r } = await api.post(`${base}/items`, { items: op.items, force: true, client_id: op.id });
            r.added.forEach((it, n) => { ids[`tmp:${op.id}:${n}`] = it.id; });
          } else {
            const real = ids[op.itemId] || op.itemId;
            if (!real.startsWith("tmp:")) await api.patch(`${base}/items/${real}`, { checked: op.checked });
          }
        } catch (e) { if (isNetErr(e)) break; }
        dequeue(op.id);
        syncQueue();
      }
    } finally { flushing.current = false; }
    await fetchNow();
  }, [base, listId, fetchNow, syncQueue]);

  useEffect(() => { fetchNow(); }, [fetchNow]);
  useEffect(() => { if (online) flush(); }, [online, flush]);
  useLiveRefresh((m) => { if (m.entity) evRef.current?.(m); fetchNow(); if (m.type === "reconnected") flush(); },
    (m) => !!m.entity?.startsWith("list") && m.payload?.list_id === listId);

  const queueOp = (op) => { enqueue({ ...op, listId }); syncQueue(); return { queued: true }; };

  const add = useCallback(async (items, force = false) => {
    const op = { kind: "add", items, id: newId() };
    if (!navigator.onLine) return queueOp(op);
    try {
      const { data: r } = await api.post(`${base}/items`, { items, force, client_id: op.id });
      if (r.added.length) setData((d) => d && { ...d, items: [...d.items, ...r.added] });
      return r;
    } catch (e) {
      if (isNetErr(e)) return queueOp({ ...op, items });
      throw e;
    }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const check = useCallback(async (item, checked) => {
    setData((d) => d && { ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, checked } : i)) });
    if (!navigator.onLine || item.id.startsWith("tmp:")) return queueOp({ kind: "check", itemId: item.id, checked });
    try {
      const { data: fresh } = await api.patch(`${base}/items/${item.id}`, { checked });
      setData((d) => d && { ...d, items: d.items.map((i) => (i.id === fresh.id ? fresh : i)) });
    } catch (e) {
      if (isNetErr(e)) return queueOp({ kind: "check", itemId: item.id, checked });
      fetchNow();
      throw e;
    }
    return null;
  }, [base, fetchNow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server items with queued offline changes laid on top (marked pending).
  const items = useMemo(() => {
    if (!data) return [];
    let out = data.items.map((i) => ({ ...i }));
    queue.forEach((op) => {
      if (op.kind === "add") {
        op.items.forEach((it, n) => out.push({ id: `tmp:${op.id}:${n}`, name: it.name.trim().replace(/^./, (c) => c.toUpperCase()), qty: it.qty || null, note: it.note || null,
          aisle: null, sorting: true, checked: false, added_by: null, pending: true, created_at: new Date(op.at).toISOString() }));
      } else {
        out = out.map((i) => (i.id === op.itemId ? { ...i, checked: op.checked, checked_by: op.checked ? { name: "You" } : null, pending: true } : i));
      }
    });
    return out;
  }, [data, queue]);

  return { data, items, error, queue, online, add, check, reload: fetchNow, base };
}
